import type { ReactNode } from "react";

import { captureException } from "@/observability/sentry";
import type { Laid } from "@/content/landing/sectionLayout";
import type { LandingBlock } from "@/content/landing/types";
import type { LaidBlock } from "./LandingPageContext";
import CardsBlock from "./blocks/CardsBlock";
import CodeBlock from "./blocks/CodeBlock";
import ComparisonBlock from "./blocks/ComparisonBlock";
import FaqBlock from "./blocks/FaqBlock";
import HeroBlock from "./blocks/HeroBlock";
import LinkGridBlock from "./blocks/LinkGridBlock";
import MatrixBlock from "./blocks/MatrixBlock";
import PlansBlock from "./blocks/PlansBlock";
import QuoteBlock from "./blocks/QuoteBlock";
import SplitBlock from "./blocks/SplitBlock";
import StatsBlock from "./blocks/StatsBlock";
import {
  CheckListBlock,
  CtaBandBlock,
  HeadingBlock,
  ProseBlock,
  TabsBlock,
} from "./blocks/MiscBlocks";
import { UseCaseIndexBlock, UseCaseRowBlock } from "./blocks/UseCaseBlocks";
import TrialFormBlock from "./blocks/TrialFormBlock";
import LogoMarqueeBlock from "./blocks/LogoMarqueeBlock";
import WhyWebshareBlock from "./blocks/WhyWebshareBlock";
import DeveloperBandBlock from "./blocks/DeveloperBandBlock";
import TestimonialBlock from "./blocks/TestimonialBlock";
import FeatureListBlock from "./blocks/FeatureListBlock";
import PlanGroupsBlock from "./blocks/PlanGroupsBlock";
import PpcHeroBlock from "./blocks/PpcHeroBlock";
import {
  CustomerQuoteBlock,
  IssuePanelsBlock,
  ObjectionsBlock,
  PpcCtaBand,
  ReviewGridBlock,
  TileGalleryBlock,
} from "./blocks/PpcBlocks";

/**
 * The one place a block kind becomes an element. Every landing page is a list
 * of these and nothing else, so a page cannot acquire markup of its own without
 * first acquiring a kind here.
 *
 * `continues`: the published pages set a heading and everything under it as
 * ONE section. Ours are separate elements, so a block that carries the
 * `level: 3` the data uses for "a card inside a section" drops the padding
 * that would otherwise repeat the section break for each one.
 */
const RENDERERS: {
  [K in LandingBlock["kind"]]: (
    block: Laid<K>,
    continues: boolean
  ) => ReactNode;
} = {
  hero: (block) => <HeroBlock block={block} />,
  split: (block, continues) => (
    <SplitBlock block={block} continues={continues} />
  ),
  stats: (block) => <StatsBlock block={block} />,
  cards: (block, continues) => (
    <CardsBlock block={block} continues={continues} />
  ),
  matrix: (block) => <MatrixBlock block={block} />,
  plans: (block) => <PlansBlock block={block} />,
  linkGrid: (block) => <LinkGridBlock block={block} />,
  useCaseRow: (block) => <UseCaseRowBlock block={block} />,
  useCaseIndex: (block) => <UseCaseIndexBlock block={block} />,
  checkList: (block) => <CheckListBlock block={block} />,
  prose: (block) => <ProseBlock block={block} />,
  quote: (block) => <QuoteBlock block={block} />,
  ctaBand: (block) =>
    block.ctaBand === "ppc" ? (
      <PpcCtaBand block={block} />
    ) : (
      <CtaBandBlock block={block} />
    ),
  heading: (block) => <HeadingBlock block={block} />,
  tabs: (block) => <TabsBlock block={block} />,
  code: (block) => <CodeBlock block={block} />,
  comparison: (block) => <ComparisonBlock block={block} />,
  faq: (block) => <FaqBlock block={block} />,
  whyWebshare: (block) => <WhyWebshareBlock block={block} />,
  developer: (block) => <DeveloperBandBlock block={block} />,
  testimonial: (block) => <TestimonialBlock block={block} />,
  featureList: (block) => <FeatureListBlock block={block} />,
  planGroups: (block) => <PlanGroupsBlock block={block} />,
  ppcHero: (block) => <PpcHeroBlock block={block} />,
  customerQuote: (block) => <CustomerQuoteBlock block={block} />,
  objections: (block) => <ObjectionsBlock block={block} />,
  reviewGrid: (block) => <ReviewGridBlock block={block} />,
  issuePanels: (block) => <IssuePanelsBlock block={block} />,
  tileGallery: (block) => <TileGalleryBlock block={block} />,
  trialForm: (block) => <TrialFormBlock block={block} />,
  logoMarquee: (block) => <LogoMarqueeBlock block={block} />,
};

function LandingBlock({ block }: { block: LaidBlock }) {
  const continues = "level" in block && block.level === 3;
  // A kind the map does not know is reached only from the CMS, whose kinds
  // the type cannot check. Every renderer is typed to its own kind; picking
  // one by the union's kind is the one place that has to widen.
  const render = RENDERERS[block.kind] as
    | ((block: LaidBlock, continues: boolean) => ReactNode)
    | undefined;
  if (!render) {
    captureException(
      new Error(`Unknown landing block kind: ${(block as LandingBlock).kind}`)
    );
    return null;
  }
  return <>{render(block, continues)}</>;
}

/**
 * A run of the page's blocks, as `layOut` handed them over: the CMS holds the
 * words and pictures, `sectionLayout` holds how the published page draws them,
 * and by here the two have met.
 */
export default function LandingBlocks({
  blocks,
}: {
  blocks: ReadonlyArray<LaidBlock>;
}) {
  return (
    <>
      {blocks.map((block, index) => (
        <LandingBlock key={`${block.kind}-${index}`} block={block} />
      ))}
    </>
  );
}
